import * as React from 'react';
import {
  MdAdd,
  MdApartment,
  MdBlock,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdEdit,
  MdPauseCircle,
  MdSearch,
  MdVisibility,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { useNavigate } from 'react-router-dom';
import { getAllLoginCreds, deleteLoginCreds } from '../../Services/LoginCredsService';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';

const STATUS_OPTIONS = ['All', 'Active', 'Inactive'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
  "Yet To Get": { backgroundColor: 'var(--hc-status-warning-bg)', color: 'var(--hc-status-warning-text)' },
};

export default function LoginCredsList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewLoginCreds = permissionHas(userPermissionCodes, PermissionCodes.LOGIN_CREDS_VIEW, decodedToken);
  const canCreateLoginCred = permissionHas(userPermissionCodes, PermissionCodes.LOGIN_CREDS_CREATE, decodedToken);
  const canEditLoginCred = permissionHas(userPermissionCodes, PermissionCodes.LOGIN_CREDS_EDIT, decodedToken);
  const canDeleteLoginCred = permissionHas(userPermissionCodes, PermissionCodes.LOGIN_CREDS_DELETE, decodedToken);

  const isSuperAdmin = isSuperAdminUser(decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const loadLoginCreds = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllLoginCreds(token);
      if (response?.is_success) {
        const creds = response?.data?.login_creds || [];
        const summary = response?.status_summary || null;
        setRows(creds);
        setStatusSummary(summary);
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setRows([]);
        setStatusSummary(null);
      } else {
        setRows([]);
        setStatusSummary(null);
      }
    } catch (error) {
      console.error(error);
      setRows([]);
      setStatusSummary(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadLoginCreds();
    }
  }, [token, loadLoginCreds]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }

    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.id || !token) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError('');
      const response = await deleteLoginCreds(deleteTarget.id, token);
      if (response?.is_success) {
        await loadLoginCreds();
        setDeleteTarget(null);
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setDeleteError('Internal Server Error. Please Contact Support Team.');
      } else {
        setDeleteError(response?.message || 'Failed to delete the credential. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setDeleteError('Something went wrong while deleting the credential.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadLoginCreds]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesStatus =
        status === 'All' || row.status_name === status;

      const matchesSearch =
        !term ||
        row.title?.toLowerCase().includes(term) ||
        row.organization_name?.toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(() => {
    const total = statusSummary?.total ?? rows.length;
    const active = statusSummary?.active ?? rows.filter((row) => row.status_id === 1).length;
    const inactive = statusSummary?.inactive ?? rows.filter((row) => row.status_id === 2).length;

    return [
      {
        label: 'Total Login Creds',
        value: total,
        icon: <MdApartment size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active Creds',
        value: active,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Inactive Creds',
        value: inactive,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ];
  }, [rows, statusSummary]);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'title',
        label: 'Title',
        minWidth: 180,
        format: (value, row) => canViewLoginCreds ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{ font: 'inherit' }}
            onClick={() =>
              navigate(APPRoutes.LOGIN_CREDS_VIEW.replace(':id', row.id), {
                state: { loginCred: row },
              })
            }
          >
            {value}
          </button>
        ) : (
          <span className="text-dark">{value}</span>
        ),
      },
      {
        id: 'organization_name',
        label: 'Organization',
        minWidth: 180,
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'account_name',
        label: 'Client',
        minWidth: 180,
      });
    }

    cols.push(
      {
        id: 'status_name',
        label: 'Status',
        minWidth: 100,
        align: 'center',
        format: (value) => (
          <span
            className="badge rounded-pill fw-bolder px-3 py-2 text-center"
            style={{
              minWidth: '96px',
              fontWeight: 1000,
              ...(STATUS_BADGE_STYLE[value] || {
                backgroundColor: 'var(--hc-status-inactive-bg)',
                color: 'var(--hc-status-inactive-text)',
              }),
            }}
          >
            {value}
          </span>
        ),
      },
    );

    if (canViewLoginCreds || canEditLoginCred || canDeleteLoginCred) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canViewLoginCreds ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() =>
                  navigate(APPRoutes.LOGIN_CREDS_VIEW.replace(':id', row.id), {
                    state: { loginCred: row },
                  })
                }
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canEditLoginCred ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => navigate(APPRoutes.LOGIN_CREDS_EDIT.replace(':id', row.id))}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
            {canDeleteLoginCred ? (
              <button
                type="button"
                title="Delete"
                className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                onClick={() => handleDeleteClick(row)}
              >
                <MdDelete size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [
    navigate,
    handleDeleteClick,
    canViewLoginCreds,
    canEditLoginCred,
    canDeleteLoginCred,
    isSuperAdmin,
  ]);

  return (
    <>
      <PageTitle title="Login Credentials - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Login Credentials</h4>
          <p className="text-secondary mb-0 small">
            Review and manage login credentials across the platform.
          </p>
        </div>
        {canCreateLoginCred ? (
          <button
            onClick={() => navigate(APPRoutes.LOGIN_CREDS_FORM)}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={18} />
            <span>Add Credential</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((index) => (
            <div key={index} className="col-12 col-sm-6 col-lg-4">
              <div className="bg-white p-4 rounded-4 shadow">
                <div className="skeleton mb-2" style={{ height: 16, width: '60%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '40%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl-4">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold ${stat.valueClass} fs-5`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3 align-items-end">
          <div className="col-12 col-md-8">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search by Title or Organization"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-4">
             <SearchableDropdown
                placeholder="Select status"
                options={STATUS_DROPDOWN_OPTIONS}
                value={status}
                onChange={setStatus}
                showSearch={true}
              />
            {/* <FormControl fullWidth size="small">
              <InputLabel id="login-creds-status-label">Status</InputLabel>
              <Select
                labelId="login-creds-status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, index) => (
                <div key={index} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Login Credential"
        entityLabel="credential"
        itemName={deleteTarget?.title || ''}
        description="You are about to permanently delete this login credential from the platform. This action cannot be undone."
        confirmText="Delete Credential"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}

